"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { ScrollArea } from "@/components/ui/scroll-area"
import { detectLocalStorageData, clearLocalStorageData, type MigrationValidationResult } from "@/lib/migration"
import { getZoneById } from "@/lib/zones"
import { CheckCircle2, XCircle, AlertCircle, Loader2, Calendar, FileText } from "lucide-react"

interface MigrationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onMigrationComplete?: () => void
}

type MigrationStep = "detect" | "confirm" | "migrating" | "success" | "error"

export function MigrationModal({ open, onOpenChange, onMigrationComplete }: MigrationModalProps) {
  const [step, setStep] = useState<MigrationStep>("detect")
  const [validationResult, setValidationResult] = useState<MigrationValidationResult | null>(null)
  const [progress, setProgress] = useState(0)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [migrationStats, setMigrationStats] = useState<{
    migrated: number
    skipped: number
    total: number
  } | null>(null)

  // Detect localStorage data when modal opens
  useEffect(() => {
    if (open) {
      const result = detectLocalStorageData()
      setValidationResult(result)

      if (result.isValid && result.entries.length > 0) {
        setStep("confirm")
      } else if (result.errors.length > 0) {
        setStep("error")
        setErrorMessage(result.errors.join(", "))
      } else {
        // No data to migrate
        setStep("error")
        setErrorMessage("Nebyla nalezena žádná data k migraci")
      }
    } else {
      // Reset state when modal closes
      setStep("detect")
      setValidationResult(null)
      setProgress(0)
      setErrorMessage(null)
      setMigrationStats(null)
    }
  }, [open])

  const handleMigrate = async () => {
    if (!validationResult || !validationResult.isValid) {
      return
    }

    setStep("migrating")
    setProgress(20)

    try {
      // Prepare entries for migration (remove id field, keep only date, zoneId, note)
      const entriesToMigrate = validationResult.entries.map(entry => ({
        date: entry.date,
        zoneId: entry.zoneId,
        note: entry.note,
      }))

      setProgress(40)

      // Call migration API
      const response = await fetch("/api/entries/migrate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ entries: entriesToMigrate }),
      })

      setProgress(70)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || "Migrace selhala")
      }

      const result = await response.json()
      setMigrationStats({
        migrated: result.migrated,
        skipped: result.skipped,
        total: result.total,
      })

      setProgress(90)

      // Clear localStorage data
      const cleared = clearLocalStorageData()
      if (!cleared) {
        console.warn("Failed to clear localStorage after migration")
      }

      setProgress(100)
      setStep("success")

      // Notify parent component
      if (onMigrationComplete) {
        onMigrationComplete()
      }
    } catch (error) {
      console.error("Migration error:", error)
      setStep("error")
      setErrorMessage(error instanceof Error ? error.message : "Nastala neočekávaná chyba")
    }
  }

  const handleSkip = () => {
    onOpenChange(false)
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        {step === "confirm" && validationResult && (
          <>
            <DialogHeader>
              <DialogTitle>Migrace dat do databáze</DialogTitle>
              <DialogDescription>
                Našli jsme {validationResult.entries.length} záznamů uložených v prohlížeči. Chceš je přenést do databáze?
              </DialogDescription>
            </DialogHeader>

            <ScrollArea className="max-h-[50vh] pr-4">
              <div className="space-y-4">
                {validationResult.warnings.length > 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        {validationResult.warnings.map((warning, idx) => (
                          <div key={idx} className="text-sm">{warning}</div>
                        ))}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Náhled dat:</h4>
                  <div className="space-y-2">
                    {validationResult.entries.slice(0, 5).map((entry, idx) => {
                      const zone = getZoneById(entry.zoneId)
                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-3 rounded-lg border bg-card text-sm"
                        >
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{entry.date}</span>
                          <span className="text-muted-foreground">•</span>
                          <span style={{ color: zone?.color }}>{zone?.name}</span>
                          {entry.note && (
                            <>
                              <FileText className="h-4 w-4 text-muted-foreground ml-auto" />
                              <span className="text-muted-foreground text-xs truncate max-w-[200px]">
                                {entry.note}
                              </span>
                            </>
                          )}
                        </div>
                      )
                    })}
                    {validationResult.entries.length > 5 && (
                      <div className="text-sm text-muted-foreground text-center py-2">
                        ... a dalších {validationResult.entries.length - 5} záznamů
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </ScrollArea>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleSkip}>
                Přeskočit
              </Button>
              <Button onClick={handleMigrate}>
                Migrovat data
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "migrating" && (
          <>
            <DialogHeader>
              <DialogTitle>Probíhá migrace...</DialogTitle>
              <DialogDescription>
                Prosím počkej, data se přenášejí do databáze.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-6">
              <div className="flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
              <Progress value={progress} className="w-full" />
              <p className="text-sm text-center text-muted-foreground">
                {progress}%
              </p>
            </div>
          </>
        )}

        {step === "success" && migrationStats && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Migrace dokončena
              </DialogTitle>
              <DialogDescription>
                Tvoje data byla úspěšně přenesena do databáze.
              </DialogDescription>
            </DialogHeader>

            <div className="py-6 space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-green-600">
                    {migrationStats.migrated}
                  </div>
                  <div className="text-sm text-muted-foreground">Migrováno</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-orange-600">
                    {migrationStats.skipped}
                  </div>
                  <div className="text-sm text-muted-foreground">Přeskočeno</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold">
                    {migrationStats.total}
                  </div>
                  <div className="text-sm text-muted-foreground">Celkem</div>
                </div>
              </div>

              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  Data v prohlížeči byla vymazána, aby nedošlo k duplicitám.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>
                Zavřít
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "error" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Chyba migrace
              </DialogTitle>
              <DialogDescription>
                Nepodařilo se migrovat data.
              </DialogDescription>
            </DialogHeader>

            <div className="py-6">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {errorMessage || "Nastala neočekávaná chyba"}
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Zavřít
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
